import React from "react";

import { Analytics, getAnalytics, logEvent, setUserProperties } from "firebase/analytics";
import { Auth, FacebookAuthProvider, getAuth } from "firebase/auth";
import { Firestore, getFirestore } from "firebase/firestore";
import { Functions, getFunctions } from "firebase/functions";
import { Installations, getInstallations } from "firebase/installations";
import { Messaging, getMessaging } from "firebase/messaging";
import { RemoteConfig, fetchAndActivate, getAll, getRemoteConfig } from "firebase/remote-config";

import LoadingPage from "../../pages/LoadingPage";

import { Constants } from "../constants";
import app from "../firebase";
import { Dev } from "../util/consoleUtil";

import { OnlyChildren } from "../../types/common";
import storageKey from "../storageKey";

export type AvailableConfigs =
  | "enabled_spott"
  | "enabled_user_spott_filter"
  | "enabled_send_spott"
  | "enabled_chat"
  | "enabled_banner_update"
  | "enabled_cafeteria_payment"
  | "enabled_rating"
  | "enabled_favorite_stop"
  | "enabled_departs"
  | "banner_clear"
  | "latest_web_version"
  | "latest_mobile_version"
  | "enabled_cafeteria"
  | "rating_prof_url"
  | "rating_comp_url"
  | "rating_version"
  | "enabled_chat_create"
  | "banner_list"
  | "bus_stop_times"
  | "bus_schedules"
  | "bus_rules"
  | "number_spott_posts";

const availableConfigTypes = {
  enabled_spott: "boolean",
  enabled_send_spott: "boolean",
  enabled_user_spott_filter: "boolean",
  enabled_chat: "boolean",
  enabled_banner_update: "boolean",
  enabled_cafeteria_payment: "boolean",
  enabled_favorite_stop: "boolean",
  latest_web_version: "string",
  latest_mobile_version: "string",
  enabled_departs: "boolean",
  enabled_rating: "boolean",
  banner_clear: "boolean",
  enabled_chat_create: "boolean",
  enabled_cafeteria: "boolean",
  number_spott_posts: "number",
  rating_prof_url: "string",
  rating_comp_url: "string",
  rating_version: "string",
  banner_list: "json",
  bus_stop_times: "json",
  bus_schedules: "json",
  bus_rules: "json",
} as {
  [key in AvailableConfigs as string]: "number" | "boolean" | "string" | "json";
};

const DEFAULTS = {
  enabled_spott: false,
  enabled_user_spott_filter: false,
  enabled_send_spott: false,
  enabled_chat: false,
  enabled_banner_update: false,
  enabled_cafeteria_payment: false,
  enabled_rating: false,
  enabled_favorite_stop: true,
  enabled_departs: true,
  enabled_cafeteria: false,
  enabled_chat_create: false,
  latest_web_version: "",
  latest_mobile_version: "",
  banner_clear: false,
  number_spott_posts: 6,
  rating_prof_url:
    "https://raw.githubusercontent.com/juniordnts/professor-rank/master/rank_by_professor.json",
  rating_comp_url:
    "https://raw.githubusercontent.com/juniordnts/professor-rank/master/rank_by_componente_ref.json",
  rating_version: "v1.0",
  banner_list: [],
  bus_stop_times: Constants.busStopTimes,
  bus_schedules: Constants.busSchedules,
  bus_rules: Constants.busRules,
};

type FirebaseContextType = {
  messaging: Messaging;
  db: Firestore;
  auth: Auth;
  functions: Functions;
  remote: RemoteConfig;
  analytics: Analytics;
  installations: Installations;
  getConfig: (config: AvailableConfigs) => number | boolean | string | any[] | any;
  forceRefreshConfigs: () => Promise<void>;
  fbProvider: FacebookAuthProvider;
  analyticLog: (key: keyof AvailableEventLogsType, value?: any) => void;
  analyticSetProperties: (key: keyof AvailablePropLogsType, value: any) => void;
};

const FirebaseContext = React.createContext<FirebaseContextType | null>(null);

var messaging: Messaging | false = false;
const db = getFirestore(app);
const auth = getAuth(app);
const functions = getFunctions(app);
const remote = getRemoteConfig(app);
const analytics = getAnalytics(app);
const installations = getInstallations(app);

type AvailablePropLogsType = {
  favorite_stop: string;
  favorite_department: string;
};

type AvailableEventLogsType = {
  select_favorite_stop: { stop: string };
  click_next_bus: { bus: "cet" | "inv" | "rei" | "dir" };
  click_other_bus: { bus: "cet" | "inv" | "rei" | "dir" };
  click_depart: { bus: "cet" | "inv" | "rei" | "dir" };
  click_rating_tab: undefined;
  click_rating_search: { search: string };
  click_food_tab: undefined;
  click_spott_tab: undefined;
  click_ru_info: { meal: "dinner" | "lunch" };
  open_help_page: undefined;
  click_help_shirt_location: undefined;
  click_help_wifi_tutorial: undefined;
  click_help_bus_map: { bus: "cet" | "inv" | "rei" | "dir" | "maps" };
  click_help_route: { from: ""; to: "" };
};

const fbProvider = new FacebookAuthProvider();

try {
  messaging = getMessaging(app);
} catch (error) {
  console.log(...new Dev("Error getting Firebase messaging", error).Error());
}

export function FirebaseProvider({ children }: OnlyChildren) {
  const [loadingConfig, setLoadingConfig] = React.useState(true);

  const [remoteDefaults, setRemoteDefaults] = React.useState(DEFAULTS);

  const setupParamsToFetchConfig = async (hardReset: boolean = false) => {
    try {
      const hasCrashed = !!window.localStorage.getItem(storageKey.CRASH_COUNT);
      const needHardReset = hasCrashed || hardReset;

      if ("cordova" in window) {
        (window as any).FirebasePlugin.setConfigSettings(30, needHardReset ? 0 : 3600 * 4);
        (window as any).FirebasePlugin.setDefaults(remoteDefaults);
      } else {
        remote.settings.minimumFetchIntervalMillis = needHardReset ? 0 : 3600 * 1000 * 4;
        remote.defaultConfig = remoteDefaults as any;
      }

      await fetchConfigValues();
    } catch (error) {
      console.log(...new Dev("setupParamsToFetchConfig Error", error).Error());
    } finally {
      setLoadingConfig(false);
      window.localStorage.removeItem(storageKey.CRASH_COUNT);
    }
  };

  const fetchConfigValues = async () => {
    if ("cordova" in window) {
      await new Promise((resolve) => {
        (window as any).FirebasePlugin.fetchAndActivate(
          (activated: boolean) => {
            console.log(...new Dev("fetchConfigValues fetchAndActivate:", activated).Success());
            resolve(undefined);
          },
          (err: Error) => {
            console.log(...new Dev("fetchConfigValues fetchAndActivate:", err).Error());
            resolve(undefined);
          }
        );
      });
    } else {
      await fetchAndActivate(remote).catch((err) => {
        console.log(...new Dev("fetchAndActivate:", err).Error());
      });
    }

    await setConfigValues();
  };

  const setConfigValues = async () => {
    if ("cordova" in window) {
      (window as any).FirebasePlugin.getAll(
        async (values: any) => {
          let newValues = { ...remoteDefaults };
          for (let key in newValues) {
            try {
              let valueType = availableConfigTypes[key];
              (newValues as any)[key] =
                valueType === "boolean"
                  ? JSON.parse(values[key])
                  : valueType === "number"
                  ? Number(values[key])
                  : valueType === "string"
                  ? String(values[key])
                  : valueType === "json"
                  ? JSON.parse(values[key])
                  : undefined;
            } catch (error) {
              (newValues as any)[key] = (DEFAULTS as any)[key];
              console.log(
                ...new Dev(
                  `setConfigValues: Error Setting Config [${key}]. Using Default Value`,
                  error
                ).Error()
              );
            }
          }
          setRemoteDefaults(newValues);
        },
        (err: Error) => {
          console.log(...new Dev("setConfigValues getAll:", err).Error());
        }
      );
    } else {
      const allValues = getAll(remote);
      let newValues = { ...remoteDefaults };

      for (let key in newValues) {
        try {
          let valueType = availableConfigTypes[key];
          (newValues as any)[key] =
            valueType === "boolean"
              ? allValues[key].asBoolean()
              : valueType === "number"
              ? allValues[key].asNumber()
              : valueType === "string"
              ? allValues[key].asString()
              : valueType === "json"
              ? JSON.parse(allValues[key].asString())
              : undefined;
        } catch (error) {
          (newValues as any)[key] = (DEFAULTS as any)[key];
          console.log(
            ...new Dev(
              `setConfigValues: Error Setting Config [${key}]. Using Default Value`,
              error
            ).Error()
          );
        }
      }

      setRemoteDefaults(newValues);
    }
  };

  const forceRefreshConfigs = async () => {
    await setupParamsToFetchConfig(true);
  };

  //

  const getConfig = (config: AvailableConfigs) => {
    return remoteDefaults[config];
  };

  const analyticLog = (key: keyof AvailableEventLogsType, value?: any) => {
    try {
      if (value === undefined) {
        if ("cordova" in window) {
          (window as any).FirebasePlugin.logEvent(key);
        } else {
          logEvent(analytics, key);
        }
      } else {
        if ("cordova" in window) {
          (window as any).FirebasePlugin.logEvent(key, value);
        } else {
          logEvent(analytics, key, value);
        }
      }
    } catch (err) {
      console.log(...new Dev("analyticLog:", err).Error());
    }
  };

  const analyticSetProperties = (key: keyof AvailablePropLogsType, value: any) => {
    try {
      if ("cordova" in window) {
        (window as any).FirebasePlugin.setUserProperty(key, value);
      } else {
        setUserProperties(analytics, { [key]: value });
      }
    } catch (err) {
      console.log(...new Dev("analyticSetProperties:", err).Error());
    }
  };

  React.useEffect(() => {
    setupParamsToFetchConfig();
  }, []);

  const value = {
    messaging,
    db,
    auth,
    functions,
    remote,
    analytics,
    installations,
    getConfig,
    forceRefreshConfigs,
    fbProvider,
    analyticLog,
    analyticSetProperties,
  };

  return (
    <FirebaseContext.Provider value={value as FirebaseContextType}>
      {loadingConfig ? <LoadingPage /> : children}
    </FirebaseContext.Provider>
  );
}

export default function useFirebase() {
  return React.useContext(FirebaseContext) as FirebaseContextType;
}
